'use client';

import { ArrowRight, Loader2, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ProcessorTypeOption } from '@/app/processors/create/processor-metadata';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SetupExperienceProcessorPickerProps {
  options: ProcessorTypeOption[];
  onContinue: (type: string) => void | Promise<void>;
  creatingType: string | null;
}

export function SetupExperienceProcessorPicker({
  options,
  onContinue,
  creatingType,
}: SetupExperienceProcessorPickerProps) {
  const { t } = useTranslation(['setup', 'processors']);

  if (options.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[color-mix(in_oklch,var(--border)_80%,transparent)] px-4 py-10 text-center">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--muted)_35%,transparent)] text-[color-mix(in_oklch,var(--muted-foreground)_88%,transparent)]"
          aria-hidden
        >
          <Search className="size-5" strokeWidth={1.75} />
        </span>
        <h3 className="text-foreground m-0 text-[0.9375rem] font-semibold">
          {t('processors:createPage.noResultsTitle')}
        </h3>
        <p className="m-0 max-w-[20rem] text-[0.8125rem] leading-[1.45] text-[color-mix(in_oklch,var(--muted-foreground)_92%,transparent)]">
          {t('processors:createPage.noResultsDescription')}
        </p>
      </div>
    );
  }

  return (
    <motion.ul
      className="m-0 grid list-none grid-cols-[minmax(0,1fr)] gap-2.5 p-0 sm:grid-cols-2 sm:gap-3 [@media(min-width:1180px)]:grid-cols-4 [@media(min-width:900px)]:grid-cols-3"
      role="listbox"
      aria-label={t('processors:selectProcessorType')}
      variants={SETUP_PATH_MOTION.list}
      initial="hidden"
      animate="show"
    >
      {options.map((processor) => {
        const isCreating = creatingType === processor.value;
        const isDisabled = Boolean(creatingType);

        return (
          <motion.li key={processor.value} variants={SETUP_PATH_MOTION.option} className="min-w-0">
            <button
              type="button"
              role="option"
              aria-selected={isCreating}
              disabled={isDisabled}
              className={cn(
                'group/picker flex min-h-[4.75rem] w-full cursor-pointer items-center gap-3.5 rounded-xl border px-4 py-3.5 text-left shadow-[0_1px_0_color-mix(in_oklch,var(--foreground)_3%,transparent)] transition-[border-color,background,box-shadow,transform] duration-200 focus-visible:border-[var(--crm-selection-border-strong)] focus-visible:shadow-[0_0_0_3px_var(--crm-selection-ring)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-[0.72]',
                isCreating
                  ? 'border-[var(--crm-selection-border-strong)] bg-[var(--crm-selection-bg-strong)] shadow-[0_0_0_1px_var(--crm-selection-ring),0_1px_0_color-mix(in_oklch,var(--foreground)_3%,transparent)]'
                  : 'bg-card text-foreground border-[color-mix(in_oklch,var(--border)_72%,transparent)] hover:enabled:border-[var(--crm-selection-hover-border)] hover:enabled:bg-[color-mix(in_oklch,var(--muted)_16%,var(--card))]',
              )}
              onClick={() => void onContinue(processor.value)}
            >
              <span
                className={cn(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border',
                  isCreating
                    ? 'border-[var(--crm-selection-icon-border)] bg-[var(--crm-selection-icon-bg)]'
                    : 'border-[color-mix(in_oklch,var(--border)_65%,transparent)] bg-[color-mix(in_oklch,var(--muted)_24%,var(--background))]',
                )}
                aria-hidden
              >
                <img
                  src={processor.logo}
                  alt=""
                  className="block h-[1.375rem] w-[1.375rem] object-contain"
                  draggable={false}
                />
              </span>

              <span className="flex min-w-0 flex-1 flex-col gap-[0.2rem]">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-foreground text-sm font-semibold leading-[1.25] tracking-[-0.01em]">
                    {processor.label}
                  </span>
                  {processor.isBeta ? (
                    <Badge
                      variant="secondary"
                      className="h-[1.125rem] px-1.5 text-[0.5625rem] font-bold tracking-[0.04em]"
                    >
                      BETA
                    </Badge>
                  ) : null}
                </span>
                <span className="block overflow-hidden text-xs leading-[1.4] text-[color-mix(in_oklch,var(--muted-foreground)_92%,transparent)] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]">
                  {processor.description}
                </span>
              </span>

              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-[color,background] duration-150',
                  isCreating
                    ? 'text-primary bg-[color-mix(in_oklch,var(--primary)_12%,transparent)]'
                    : 'bg-[color-mix(in_oklch,var(--muted)_30%,transparent)] text-[color-mix(in_oklch,var(--muted-foreground)_80%,transparent)]',
                  !isCreating &&
                    !isDisabled &&
                    'group-hover/picker:text-primary group-hover/picker:bg-[color-mix(in_oklch,var(--primary)_12%,transparent)]',
                )}
                aria-hidden
              >
                {isCreating ? (
                  <Loader2 className="size-4 animate-spin" strokeWidth={2} />
                ) : (
                  <ArrowRight className="size-4" strokeWidth={2.25} />
                )}
              </span>
            </button>
          </motion.li>
        );
      })}
    </motion.ul>
  );
}
